import React, { useState } from 'react';
import { siteData } from '../data.js';
import SEO from '../components/SEO.jsx';
import { useCmsCollection } from '../lib/cms.js';
import { Users, Target, CheckCircle2, Heart, Shield, Scale, Globe, Coins, AlertTriangle, Briefcase, HeartHandshake, Sparkles, ArrowRight, X, Brain, MapPin, Award, Compass, LifeBuoy } from 'lucide-react';

export default function About() {
  const [expandedMember, setExpandedMember] = useState(null);

  const { data: rawBestyrelse } = useCmsCollection('bestyrelse', siteData.bestyrelse || []);
  const { data: rawFrivillige } = useCmsCollection('frivillige', siteData.frivillige || []);
  const { data: rawForretningsudvalg } = useCmsCollection('forretningsudvalg', siteData.forretningsudvalg || []);

  const bestyrelseList = rawBestyrelse.map((m) => {
    const d = m.data || m;
    return {
      name: d.navn || d.name,
      role: d.rolle_tekst || d.role,
      email: d.email,
      bio: d.bio,
      image: d.billede?.[0] || d.image,
    };
  });

  const frivilligeList = rawFrivillige.map((f) => {
    if (typeof f === 'string') return f;
    const d = f.data || f;
    return d.navn || d.name || '';
  }).filter(Boolean);

  const forretningsudvalgList = rawForretningsudvalg.map((f) => {
    const d = f.data || f;
    return {
      name: d.navn || d.name || (typeof f === 'string' ? f : ''),
      role: d.rolle || d.role || d.omraade || '',
    };
  }).filter((item) => Boolean(item.name));

  return (
    <div className="pt-20 pb-0">
      <SEO
        title="Om os | Vores mission, vision og værdier"
        description="Lær foreningen Tak For Din Indsats (TFDI) at kende. Læs om vores bestyrelse, ambassadører, vision og det frivillige arbejde for Danmarks frontpersonale."
      />
      {/* Hero / Header Section */}
      <div className="relative bg-slate-900 text-white py-20 mb-16 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(220,38,38,0.15),transparent_50%)] overflow-hidden">
          <img 
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/512cb13c-28f0-4ecb-9d9d-4ac03cf77200/danish-veterans-front-personnel-solidarity-warm.png" 
            alt="Baggrund" 
            className="absolute inset-0 w-full h-full object-cover opacity-20 mix-blend-overlay pointer-events-none"
          />
          <div className="absolute inset-0 bg-slate-950/60" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-4">
            Om Os
          </h1>
          <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto font-medium leading-relaxed">
            Vi er dedikerede til at hjælpe mennesker i krise
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-20 items-center">
          <div className="space-y-6">
            <span className="inline-block bg-red-50 text-red-600 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest">
              Hvem er vi
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight">
              Vi kæmper for dem, der har og forsat kæmper for Danmark
              <svg className="inline-block h-6 w-8 ml-2.5 align-middle shadow-sm rounded-sm border border-slate-100" viewBox="0 0 37 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="37" height="28" fill="#C8102E"/>
                <path d="M12 0H16V28H12V0Z" fill="white"/>
                <path d="M0 12H37V16H0V12Z" fill="white"/>
              </svg>
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              TFDI er en frivillig almennyttig forening, der arbejder utrætteligt for at forbedre vilkårene for de mennesker, der hver dag sætter deres eget liv og helbred på spil for Danmarks sikkerhed og tryghed.
            </p>
            <p className="text-slate-600 leading-relaxed">
              Vi er dedikerede til at skabe bedre anerkendelse og redde dem som dagligt redder Danmark. Veteraner, forsvars- og frontpersonel ansatte
            </p>
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-900">Vores primære indsatsområder:</h3>
              <ul className="space-y-3">
                {[
                  "Bedre anerkendelse og politisk bevågenhed og ansvarstagen",
                  "Bedre og flere helhedsorienteret behandlingsmuligheder med, forskning, implementering af disse samt dækningstilsagn til behandlingsforløb",
                  "Det hele menneske og familien bag må ikke stå alene når skaden er sket."
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3 text-slate-600">
                    <CheckCircle2 size={18} className="text-red-600 shrink-0 mt-1" />
                    <span className="font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 rounded-3xl bg-red-50/50 rotate-2 scale-105 -z-10" />
            <img 
              src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/512cb13c-28f0-4ecb-9d9d-4ac03cf77200/danish-volunteers-mentors-solidarity.png" 
              alt="Frivillige" 
              className="rounded-3xl shadow-2xl w-full h-[400px] object-cover"
            />
            <div className="absolute -bottom-6 -right-6 bg-red-600 p-8 rounded-3xl text-white shadow-xl hidden md:block text-center">
              <p className="text-3xl font-black mb-1 italic">100%</p>
              <p className="text-sm font-bold opacity-90 uppercase tracking-widest italic">Frivillig drevet</p>
            </div>
          </div>
        </div>

        {/* Formål, Mission & Vision Section */}
        <div className="mb-24">
          <div className="text-left max-w-3xl mb-6">
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4">
              Formål, Mission & Vision
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Formål er også at skabe anerkendelse og etableringen af et tilstrækkeligt statsgaranteret sikkerhedsnet under ansatte i forsvar, Brand/redning, Ambulance, Sygehus, Politi og Danmarks fængsler og dennes pårørende og børn.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Formål Card */}
            <div className="bg-white border border-slate-100 rounded-3xl p-8 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between relative overflow-hidden group">
              <div className="absolute top-0 left-0 w-2 h-full bg-red-600 transition-all duration-300 group-hover:w-3" />
              <div>
                <div className="w-12 h-12 bg-red-50 text-red-600 rounded-2xl flex items-center justify-center mb-6">
                  <Shield size={24} />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Formål</h3>
                <p className="text-slate-600 text-sm md:text-base leading-relaxed mb-4">
                  <strong className="text-slate-900">Tak For Din Indsats</strong> arbejder for at sikre veteraner, forsvarsansatte og frontpersonel samt deres familier <span className="text-red-600 font-semibold">lige adgang til hjælp, støtte og helhedsorienterede behandlings indsatser</span> ved arbejdsrelaterede belastningsreaktioner – fysisk, psykisk og kognitivt. Foreningen arbejder særligt for, at ingen falder mellem systemerne på grund af faggruppe, geografi eller myndighedsgrænser.
                </p>
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <p className="text-slate-600 text-sm md:text-base leading-relaxed">
                    <span className="text-red-600 font-semibold">Yderligere formål:</span> Yde dækningstilsagn til behandlingsforløb ved behandlinger som er veldokumenterede men ikke endnu er tilgængelig i det etablerde behandlingssystem.
                  </p>
                </div>
              </div>
            </div>

            {/* Mission Card */}
            <div className="bg-white border border-slate-100 rounded-3xl p-8 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between relative overflow-hidden group">
              <div className="absolute top-0 left-0 w-2 h-full bg-red-600 transition-all duration-300 group-hover:w-3" />
              <div>
                <div className="w-12 h-12 bg-red-50 text-red-600 rounded-2xl flex items-center justify-center mb-6">
                  <Target size={24} />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Mission</h3>
                <p className="text-slate-600 text-sm md:text-base leading-relaxed">
                  At hjælpe veteraner, forsvarsansatte og frontpersonel med arbejdsrelaterede belastningsreaktioner til adgang til en <span className="text-red-600 font-semibold">ligelig, effektiv og tværfaglig helhedsorienteret indsats</span>, herunder behandlingstilbud som ikke nødvendigvis er tilgængelige i det offentlige system. Samtidig arbejder foreningen for bedre koordinering mellem myndigheder, øget støtte til pårørende og børn samt forebyggelse af isolation og selvmord.
                </p>
              </div>
            </div>

            {/* Vision Card */}
            <div className="bg-slate-900 text-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 flex flex-col justify-between relative overflow-hidden group">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(220,38,38,0.15),transparent_50%)]" />
              <div className="relative z-10">
                <div className="w-12 h-12 bg-red-600 text-white rounded-2xl flex items-center justify-center mb-6">
                  <Sparkles size={24} />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">Vision</h3>
                <p className="text-slate-300 text-sm md:text-base leading-relaxed mb-4">
                  Et Danmark hvor vi alle står sammen og anerkender alle der har sat sig i front for samfundet.
                </p>
                <p className="text-slate-300 text-sm md:text-base leading-relaxed">
                  TFDI ønsker at staten tager ansvar og sikre et <span className="text-red-400 font-semibold">veletableret sikkerhedsnet</span>. Pengene skal følge de ansatte og den nationale indsats må ikke være afhængig af blok tilskud og eller blokpolitik eller geografi.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Vores Mærkesager */}
        <div className="mb-24">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block bg-red-50 text-red-600 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-3">
              Vores mærkesager
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4">
              Vores Løsninger og Fokusområder
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              En national anerkendelse af frontpersonale fra Forsvar, Politi, Sygehus, Fængsel, Brand/Redning eller Ambulance, hvor Danmark står sammen og anerkender, støtter og samler ind til dem af vores dagligdags helte.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
            {/* 1. Helhedsorienteret, ligelig og pårørende fokus */}
            <div className="bg-white border border-slate-100 rounded-3xl p-8 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between lg:col-span-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="space-y-3">
                  <div className="w-12 h-12 bg-red-50 text-red-600 rounded-2xl flex items-center justify-center mb-4">
                    <HeartHandshake size={24} />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">Helhedsorienteret behandling</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    Alle skal have adgang til behandling, der adresserer både de fysiske og psykiske konsekvenser af arbejdsrelaterede skader.
                  </p>
                </div>
                <div className="space-y-3">
                  <div className="w-12 h-12 bg-red-50 text-red-600 rounded-2xl flex items-center justify-center mb-4">
                    <Scale size={24} />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">Ligelig behandling</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    Der må ikke være forskelle i behandlingstilbud baseret på bopæl, region eller hvilken gruppe man tilhører (veteran eller frontpersonale).
                  </p>
                </div>
                <div className="space-y-3">
                  <div className="w-12 h-12 bg-red-50 text-red-600 rounded-2xl flex items-center justify-center mb-4">
                    <Users size={24} />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">Fokus på pårørende og børn</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    Vi arbejder for støtte i hjemmet og akut hjælp, så ingen pårørende står alene med et skadet familiemedlem.
                  </p>
                </div>
              </div>
            </div>

            {/* 2. Specifikke Indsatser */}
            <div className="bg-slate-900 text-white rounded-3xl p-8 shadow-xl lg:col-span-2 flex flex-col justify-between relative overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(220,38,38,0.1),transparent_50%)]" />
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-red-600 text-white rounded-xl flex items-center justify-center">
                    <Sparkles size={20} />
                  </div>
                  <h3 className="text-2xl font-bold">Specifikke Indsatser</h3>
                </div>
                <div className="space-y-6">
                  <div>
                    <h4 className="font-bold text-red-400 mb-1 text-base">Forebyggelse af isolation og selvmord</h4>
                    <p className="text-slate-300 text-sm leading-relaxed">
                      Vi arbejder for at sikre, at ingen bliver efterladt uden hjælp, så vi kan stoppe den tragiske stigning i selvmord blandt veteraner og frontpersonale.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-bold text-red-400 mb-1 text-base">Behandling af dobbeltproblemstillinger</h4>
                    <p className="text-slate-300 text-sm leading-relaxed">
                      Mange, der lider af PTSD, har også misbrugsproblemer. Vi arbejder for en samlet indsats, der kan adressere begge udfordringer.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-bold text-red-400 mb-1 text-base">Adgang til alternative behandlinger</h4>
                    <p className="text-slate-300 text-sm leading-relaxed">
                      Vi støtter behandlinger, som ikke er en del af det offentlige sundhedssystem, såsom SGB, Float, Kropsterapi, Klinisk genoptræning, og Psykoterapi for PTSD. TFDI arbejder også for gratis online integrationsterapi for dem, der modtager denne supplerende SGB behandling.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* 3. Behandling af Sekundærtraumer hos Pårørende */}
            <div className="bg-white border border-slate-100 rounded-3xl p-8 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 bg-red-50 text-red-600 rounded-2xl flex items-center justify-center mb-6">
                  <Heart size={24} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Behandling af Sekundærtraumer hos Pårørende</h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-6">
                  Familier til skadede veteraner og frontpersonale lider ofte af sekundære traumer. TFDI arbejder for at reducere disse traumer gennem:
                </p>
                <ul className="space-y-3">
                  {[
                    "Tidlig indsats og støtte i hjemmet.",
                    "Specialiserede behandlingsmetoder, som også inkluderer børn og familier, der lever med skadede.",
                    "Tværfaglige indsatser, koordinering og støtte."
                  ].map((bullet, i) => (
                    <li key={i} className="flex items-start gap-3 text-slate-600 text-sm">
                      <CheckCircle2 size={16} className="text-red-600 shrink-0 mt-0.5" />
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* 4. Sociallovgivningen og Bedre Samarbejde */}
            <div className="bg-white border border-slate-100 rounded-3xl p-8 shadow-sm hover:shadow-md transition-all duration-300 lg:col-span-3">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
                <div className="lg:col-span-1">
                  <div className="w-12 h-12 bg-red-50 text-red-600 rounded-2xl flex items-center justify-center mb-6">
                    <Scale size={24} />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">Sociallovgivningen og Bedre Samarbejde</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    Den nuværende fortolkning af sociallovgivning, særligt servicelovens paragraffer 107, 110, 102, 85 og 85a, er fragmenteret, hvilket gør det svært for kommuner, regioner og Veterancenteret at samarbejde effektivt. Vi forslår:
                  </p>
                </div>
                <div className="lg:col-span-2 bg-slate-50 rounded-2xl p-6 md:p-8 flex flex-col justify-center">
                  <ul className="space-y-4">
                    {[
                      "En bedre koordination mellem myndigheder, så det ikke er op til den enkelte skadede at være sin egen sagsbehandler.",
                      "Indførelse af en veteran- og frontpersonale koordinator i hver kommune for at sikre ensartet og specialiseret hjælp på tværs af hele landet.",
                      "En fælles forståelse af lovgivningen så mulighederne indenfor denne fortolkes ens.",
                      "Entydigt lovgivning på veteran- og frontpersonelområdet."
                    ].map((bullet, i) => (
                      <li key={i} className="flex items-start gap-3 text-slate-700 text-sm md:text-base">
                        <CheckCircle2 size={18} className="text-red-600 shrink-0 mt-1" />
                        <span className="font-medium">{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>


        {/* Mød Bestyrelsen Section */}
        <div className="mb-24 border-t border-slate-100 pt-16">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4">
              Mød Bestyrelsen i Tak For Din Indsats
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Vores bestyrelse arbejder frivilligt for at sikre den bedste støtte til dem, der passer på Danmark.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {bestyrelseList && bestyrelseList.map((member, index) => {
              return (
                <div 
                  key={index} 
                  className="bg-white rounded-3xl border border-slate-100 shadow-xl shadow-slate-100/50 hover:shadow-2xl hover:shadow-slate-200/50 transition-all duration-300 p-8 flex flex-col h-full group"
                >
                  <div className="flex flex-col items-center text-center mb-6">
                    <div className="w-24 h-24 bg-slate-50 rounded-full flex items-center justify-center mb-4 border border-slate-100 overflow-hidden shrink-0 shadow-inner group-hover:scale-105 transition-transform duration-300">
                      <img 
                        src={siteData.logo} 
                        alt="Tak For Din Indsats Logo" 
                        className="w-16 h-16 object-contain"
                      />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-1 group-hover:text-red-600 transition-colors">{member.name}</h3>
                    <p className="text-sm font-bold text-red-600 mb-2">{member.role}</p>
                  </div>

                  {member.bio && (
                    <div className="flex flex-col flex-grow justify-between">
                      <p className="text-slate-600 text-sm leading-relaxed text-center mb-6 line-clamp-3 italic">
                        "{member.bio.replace(/^[”"“' ]+/, '').slice(0, 110)}..."
                      </p>
                      
                      <button
                        onClick={() => setExpandedMember(member)}
                        className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-slate-50 hover:bg-red-50 text-slate-700 hover:text-red-600 rounded-2xl font-bold text-sm transition-all duration-200 border border-slate-100 hover:border-red-100 cursor-pointer"
                      >
                        Læs hele præsentationen
                        <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>


        {/* Forretningsudvalg & Mød de frivillige Section */}
        <div className="mb-24 border-t border-slate-100 pt-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            {/* Forretningsudvalg */}
            <div className="flex flex-col">
              <div className="text-left mb-8 sm:min-h-[100px]">
                <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-3">
                  Forretningsudvalg i TFDI
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed">
                  Forretningsudvalget varetager foreningens strategiske arbejde
                </p>
              </div>

              <div className="bg-slate-50 rounded-3xl border border-slate-100 p-8 shadow-sm">
                <div className="divide-y divide-slate-100">
                  {forretningsudvalgList && forretningsudvalgList.map((member, index) => (
                    <div key={index} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                      <div className="w-2 h-2 rounded-full bg-red-600 shrink-0" />
                      <div className="text-lg text-slate-800">
                        <strong className="font-semibold text-slate-900">{member.name}</strong>
                        {member.role && (
                          <span className="font-normal text-slate-600"> – {member.role}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Mød de frivillige */}
            <div className="flex flex-col">
              <div className="text-left mb-8 sm:min-h-[100px]">
                <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-3">
                  Mød de frivillige i TFDI
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed">
                  De frivillige hjælper og er en uvurderlig støtte
                </p>
              </div>

              <div className="bg-slate-50 rounded-3xl border border-slate-100 p-8 shadow-sm">
                <div className="divide-y divide-slate-100">
                  {frivilligeList && frivilligeList.map((name, index) => (
                    <div key={index} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                      <div className="w-2 h-2 rounded-full bg-red-600 shrink-0" />
                      <span className="text-lg font-semibold text-slate-800">{name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Beautiful Modal for Board Member Bio */}
        {expandedMember && (
          <div 
            className="fixed inset-0 bg-slate-950/60 backdrop-blur-md z-50 flex items-center justify-center p-4 md:p-6"
            onClick={() => setExpandedMember(null)}
          >
            <div 
              className="bg-white rounded-[2.5rem] shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto relative border border-slate-100 p-6 md:p-10"
              onClick={(e) => e.stopPropagation()}
            >
              <button 
                onClick={() => setExpandedMember(null)}
                className="absolute top-6 right-6 text-slate-400 hover:text-slate-600 transition-colors bg-slate-50 hover:bg-slate-100 p-2.5 rounded-full cursor-pointer"
                aria-label="Luk"
              >
                <X size={20} />
              </button>

              <div className="flex flex-col items-center md:items-start md:flex-row gap-6 border-b border-slate-100 pb-8 mb-8">
                <div className="w-24 h-24 bg-slate-50 rounded-full flex items-center justify-center border border-slate-100 overflow-hidden shrink-0 shadow-inner">
                  <img 
                    src={siteData.logo} 
                    alt="Tak For Din Indsats Logo" 
                    className="w-16 h-16 object-contain"
                  />
                </div>
                <div className="text-center md:text-left">
                  <h3 className="text-2xl md:text-3xl font-black text-slate-900 mb-1">
                    {expandedMember.name}
                  </h3>
                  <p className="text-base font-bold text-red-600 mb-3">
                    {expandedMember.role}
                  </p>
                </div>
              </div>

              <div className="space-y-4 text-slate-600 leading-relaxed text-left">
                {expandedMember.bio && expandedMember.bio.split('\n').filter(p => p.trim() !== '').map((para, pIdx) => (
                  <p key={pIdx} className="text-slate-600 text-base md:text-lg leading-relaxed">
                    {para}
                  </p>
                ))}
              </div>

              <div className="mt-8 pt-6 border-t border-slate-100">
                <button
                  onClick={() => setExpandedMember(null)}
                  className="w-full py-4 bg-slate-900 hover:bg-slate-800 text-white rounded-2xl font-bold transition-colors text-center cursor-pointer"
                >
                  Luk vindue
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
